import React, { useState } from 'react';
import { createStyles, makeStyles, withStyles, Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Checkbox from '@material-ui/core/Checkbox';

import { Product } from 'modules/commission_terms/pages/CommissionDetailsPage/CommissionDetailsPage.types';

const StyledTableCell = withStyles(() =>
  createStyles({
    head: {
      fontSize: 14,
      fontWeight: 600,
      color: '#13171B',
    },
    body: {
      fontSize: 14,
      color: '#13171B',
    },
  }),
)(TableCell);

const StyledTableRow = withStyles(() =>
  createStyles({
    root: {
      '&:nth-of-type(even)': {
        backgroundColor: '#F2F2F2',

        '&.Mui-selected': {
          backgroundColor: '#F2F2F2',

          '&:hover': {
            backgroundColor: '#F2F2F2',
          },
        },
      },

      '&.Mui-selected': {
        backgroundColor: 'unset',

        '&:hover': {
          backgroundColor: 'unset',
        },
      },
    },
  }),
)(TableRow);

const StyledCheckbox = withStyles(() =>
  createStyles({
    root: {
      '&.Mui-checked': {
        color: '#670d2d',
      },
    },
  }),
)(Checkbox);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      marginTop: theme.spacing(3),
    },
    head: {
      backgroundColor: '#F2F2F2',
    },
    table: {
      minWidth: 750,
      border: '1px solid #D9D9D9',
    },
    tableWrapper: {
      overflowX: 'auto',
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
  }),
);

const headCells = [
  { id: 'fixedRate', numeric: false, disablePadding: true, label: 'Fixed rate' },
  { id: 'product', numeric: true, disablePadding: false, label: 'Product' },
  { id: 'productCode', numeric: true, disablePadding: false, label: 'Product code' },
  { id: 'fixedRateTerm', numeric: true, disablePadding: false, label: 'Fixed rate term' },
  { id: 'maxLtv', numeric: true, disablePadding: false, label: 'Max LTV' },
];

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  rowCount: number;
}

const EnhancedTableHead: React.FC<EnhancedTableProps> = ({ classes, onSelectAllClick, numSelected, rowCount }) => (
  <TableHead className={classes.head}>
    <StyledTableRow>
      <StyledTableCell padding="checkbox">
        <StyledCheckbox
          indeterminate={numSelected > 0 && numSelected < rowCount}
          checked={numSelected === rowCount}
          onChange={onSelectAllClick}
          inputProps={{ 'aria-label': 'Select all products' }}
        />
      </StyledTableCell>
      {headCells.map((headCell) => (
        <StyledTableCell key={headCell.id} align="left" padding="normal">
          {headCell.label}
        </StyledTableCell>
      ))}
    </StyledTableRow>
  </TableHead>
);

type ProductTableProps = {
  items: Product[];
  onItemsSelected: (selectedProducts: Product[]) => void;
  selectedItems: Product[];
};

const ProductsTable: React.FC<ProductTableProps> = ({ items = [], selectedItems = [], onItemsSelected }) => {
  const classes = useStyles();
  const [selected, setSelected] = useState<Product[]>(selectedItems);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const selectedIds = selected.map((item) => item.id);
      const productsToAdd = items.filter((item) => !selectedIds.includes(item.id));

      setSelected([...selected, ...productsToAdd]);
      onItemsSelected([...selected, ...productsToAdd]);
      return;
    }

    const visibleIds = items.map((item) => item.id);
    const selectedProducts = selected.filter((item) => !visibleIds.includes(item.id));

    onItemsSelected(selectedProducts);
    setSelected(selectedProducts);
  };

  const handleClick = (product: Product) => {
    const selectedProductCodes = selected.map((p) => p.code);
    const selectedIndex = selectedProductCodes.indexOf(product.code);
    let newSelected: Product[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, product);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }

    onItemsSelected(newSelected);

    setSelected(newSelected);
  };

  const isSelected = (product: Product) => {
    const productCodes = selected.map((p) => p.code);
    return productCodes.indexOf(product.code) !== -1;
  };

  const getNumSelected = () => {
    const visibleIds = items.map((item) => item.id);
    const selectedProducts = selected.filter((item) => visibleIds.includes(item.id));

    return selectedProducts;
  };

  return (
    <div className={classes.root}>
      <div className={classes.tableWrapper}>
        <Table className={classes.table} aria-labelledby="tableTitle" size="medium">
          <EnhancedTableHead
            classes={classes}
            numSelected={getNumSelected().length as number}
            onSelectAllClick={handleSelectAllClick}
            rowCount={items.length}
          />
          <TableBody>
            {items.map((row, index) => {
              const isItemSelected = isSelected(row);
              const labelId = `enhanced-table-checkbox-${index}`;

              return (
                <StyledTableRow
                  onClick={() => handleClick(row)}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={row.code}
                  selected={isItemSelected}>
                  <StyledTableCell padding="checkbox">
                    <StyledCheckbox checked={isItemSelected} inputProps={{ 'aria-labelledby': labelId }} />
                  </StyledTableCell>
                  <StyledTableCell component="th" id={labelId}>
                    {`${row.intRate.toFixed(2)}%`}
                  </StyledTableCell>
                  <StyledTableCell align="left">{row.product}</StyledTableCell>
                  <StyledTableCell align="left">{row.code}</StyledTableCell>
                  <StyledTableCell align="left">
                    {row.fixRateTermYears ? `${row.fixRateTermYears} years` : 'Lifetime'}
                  </StyledTableCell>
                  <StyledTableCell align="left">{`${row.maxLtv}%`}</StyledTableCell>
                </StyledTableRow>
              );
            })}
            {!items.length && (
              <StyledTableRow>
                <StyledTableCell component="td" colSpan={6}>
                  No products were found matching your filters
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ProductsTable;
